import { BaseService } from "./BaseService";
import type {
  ExportJob,
  ExportJobsQuery,
  PaginatedExportJobs,
  StartExportDto,
} from "@/types/data-export";

export interface IDataExportService {
  startGuestExport(guestId: string, dto: StartExportDto): Promise<ExportJob>;
  startUserExport(userId: string, dto: StartExportDto): Promise<ExportJob>;
  getJob(jobId: string): Promise<ExportJob>;
  getJobs(query?: ExportJobsQuery): Promise<PaginatedExportJobs>;
}

/**
 * SUPER_ADMIN / HOTEL_ADMIN GDPR data-export (DSAR) API. Routes live under
 * `/admin/data-export` (the configured base URL already includes `/api`). This
 * is the read-only sibling of data-deletion — nothing is ever mutated.
 *
 * Start calls suppress the global error toast so the flow can surface 400/403/409
 * inline; the polling read does the same to avoid toast spam.
 */
class DataExportService extends BaseService implements IDataExportService {
  private readonly basePath = "/admin/data-export";

  startGuestExport(guestId: string, dto: StartExportDto): Promise<ExportJob> {
    return this.request<ExportJob>(`${this.basePath}/guests/${guestId}`, {
      method: "POST",
      body: JSON.stringify(dto),
      // SUPER_ADMIN calls carry `tenantId` and are platform-scoped (skip the
      // hotel header); HOTEL_ADMIN calls omit it and are hotel-scoped, so let
      // `x-hotel-id` through.
      skipSelectedHotelHeader: Boolean(dto.tenantId),
      disableToast: true,
    });
  }

  startUserExport(userId: string, dto: StartExportDto): Promise<ExportJob> {
    return this.request<ExportJob>(`${this.basePath}/users/${userId}`, {
      method: "POST",
      body: JSON.stringify(dto),
      skipSelectedHotelHeader: true,
      disableToast: true,
    });
  }

  getJob(jobId: string): Promise<ExportJob> {
    return this.get<ExportJob>(`${this.basePath}/jobs/${jobId}`, undefined, {
      skipSelectedHotelHeader: true,
      disableToast: true,
    });
  }

  getJobs(query?: ExportJobsQuery): Promise<PaginatedExportJobs> {
    return this.get<PaginatedExportJobs>(`${this.basePath}/jobs`, query, {
      skipSelectedHotelHeader: true,
    });
  }
}

export const dataExportService = new DataExportService();
